import { ProductDetailApi } from '@common/api/product-detail';
import { createPath } from '@common/application/utils/create-path';
import { getLocaleValue } from '@common/application/utils/get-locale-value';
import { safeArray } from '@common/application/utils/safe-array';
import { ROUTE_MAP } from '@common/constants/route-map';
import type { ProductDetailVO } from '@common/types/product-detail';
import { useAsyncEffect, useToggle } from 'ahooks';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';

import { Attrbute } from './attribute';
import { ProductSelector } from './product-selector';
import { Products } from './products';

export const DiffTable: React.FC = () => {
  const router = useRouter();
  const [open, openActions] = useToggle();
  const [products, setProducts] = useState<(ProductDetailVO | undefined)[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const cskus = useMemo(() => {
    const csku = router.query.csku as string;
    const subCskus = ((router.query.subCskus as string) || '').split(',');
    // 使子品数量始终保持3个
    subCskus.length = 3;
    return [csku, ...subCskus];
  }, [router.query.csku, router.query.subCskus]);

  const attributeList = useMemo(
    () => safeArray(products[0]?.offlineCategoryAttributeVo.attributeList),
    [products[0]?.simpleProductVo.csku]
  );

  useAsyncEffect(async () => {
    const products = await Promise.all(
      cskus.map((csku) =>
        csku
          ? ProductDetailApi.byCSKU({
              csku
            })
          : undefined
      )
    );
    setProducts(products);
  }, []);

  function syncUrl(selectedCsku: string, currentIndex: number) {
    const [csku, ...subCskus] = cskus.map((csku, index) =>
      index === currentIndex ? selectedCsku : csku
    );
    const path = createPath(ROUTE_MAP.PRODUCT_DIFF, {
      csku,
      subCskus: subCskus.join(',')
    });
    router.replace(path, undefined, {
      shallow: true
    });
  }

  async function selectProduct(selectedCsku: string) {
    const selectedProduct = await ProductDetailApi.byCSKU({
      csku: selectedCsku
    });
    setProducts(
      products.map((product, index) => (index === currentIndex ? selectedProduct : product))
    );
    syncUrl(selectedCsku, currentIndex);
  }

  function removeProduct(currentIndex: number) {
    setProducts(products.map((product, index) => (index === currentIndex ? undefined : product)));
    syncUrl('', currentIndex);
  }

  return (
    <div className="py-5 w-full bg-white">
      <div className="container-content overflow-x-scroll">
        <table border={1} cellSpacing={0} cellPadding={0}>
          <tbody>
            <Products
              products={products}
              onAdd={(index) => {
                setCurrentIndex(index);
                openActions.toggle();
              }}
              onRemove={removeProduct}
            />
            {attributeList.map((attribute) => (
              <Attrbute
                key={attribute.id}
                name={getLocaleValue(attribute.nameMap)}
                attrId={attribute.id}
                products={products}
              />
            ))}
          </tbody>
        </table>
      </div>
      <ProductSelector
        open={open}
        offlineCategoryId={products?.[0]?.simpleProductVo.offlineCategoryId}
        onConfirm={(selectedCsku) => {
          openActions.toggle();
          selectProduct(selectedCsku);
        }}
        onCancel={openActions.toggle}
      />
    </div>
  );
};
